/*
 * Biblioteca pronta do gerador de imagens: formatos, paletas, pares de fontes
 * e modelos iniciais. Tudo é aplicado sobre uma cópia do modelo.
 *
 * Coordenadas dos elementos são normalizadas (0–1) em relação ao canvas.
 * Tamanhos (fonte, raio, borda, padding) são em px na resolução final.
 */

export const FORMATS = {
  story: { key: 'story', label: 'Story', width: 1080, height: 1920, safeTop: 250, safeBottom: 340 },
  post: { key: 'post', label: 'Publicação', width: 1080, height: 1080, safeTop: 0, safeBottom: 0 },
};

/* ---------- cores ---------- */

const INK = '#1b1226';
const PURPLE = '#583a8e';
const SUN = '#ffd23f';
const RED = '#d7141a';
const PINK = '#ff5fa2';

export function readableTextColor(hex) {
  const value = (hex || '#ffffff').replace('#', '');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  if (luminance > 0.6) {
    return INK;
  }

  return '#ffffff';
}

/*
 * accent/accentText: faixas, selos e destaques. card/cardText: cartões.
 * overlay: cor do escurecimento sobre a foto.
 */
export const PALETTES = [
  { key: 'i7', name: 'i7', background: { type: 'color', color: PURPLE }, text: '#ffffff', accent: SUN, accentText: INK, card: '#ffffff', cardText: INK, overlay: '#1d0f33' },
  { key: 'plantao', name: 'Plantão', background: { type: 'color', color: RED }, text: '#ffffff', accent: '#ffffff', accentText: RED, card: '#ffffff', cardText: INK, overlay: '#2a0004' },
  { key: 'noite', name: 'Noite', background: { type: 'color', color: INK }, text: '#ffffff', accent: PINK, accentText: INK, card: '#2a2036', cardText: '#ffffff', overlay: '#0d0814' },
  { key: 'papel', name: 'Papel', background: { type: 'color', color: '#ffffff' }, text: INK, accent: PURPLE, accentText: '#ffffff', card: '#f2eff7', cardText: INK, overlay: INK },
  { key: 'sol', name: 'Sol', background: { type: 'color', color: SUN }, text: INK, accent: INK, accentText: SUN, card: '#ffffff', cardText: INK, overlay: INK },
  { key: 'chiclete', name: 'Chiclete', background: { type: 'color', color: PINK }, text: INK, accent: INK, accentText: '#ffffff', card: '#ffffff', cardText: INK, overlay: '#3b0a22' },
  { key: 'ceu', name: 'Céu', background: { type: 'color', color: '#0b3fa8' }, text: '#ffffff', accent: SUN, accentText: '#0b3fa8', card: '#ffffff', cardText: '#0b2766', overlay: '#03123a' },
  { key: 'mata', name: 'Mata', background: { type: 'color', color: '#0e5e4a' }, text: '#ffffff', accent: SUN, accentText: '#0e5e4a', card: '#ffffff', cardText: '#0b3a2e', overlay: '#03241b' },
  { key: 'por-do-sol', name: 'Pôr do sol', background: { type: 'gradient', colors: ['#ff8a1f', '#c2003a'], angle: 160 }, text: '#ffffff', accent: '#ffffff', accentText: '#c2003a', card: '#ffffff', cardText: INK, overlay: '#3a0012' },
  { key: 'neon', name: 'Neon', background: { type: 'gradient', colors: ['#3a1c71', '#d76d77'], angle: 150 }, text: '#ffffff', accent: '#ffffff', accentText: '#3a1c71', card: '#ffffff', cardText: '#3a1c71', overlay: '#1a0b33' },
];

/* ---------- fontes ---------- */

export const FONT_PAIRS = [
  { key: 'i7', name: 'Padrão', title: { font: 'Poppins', weight: 800 }, support: { font: 'Poppins', weight: 500 } },
  { key: 'moderno', name: 'Moderno', title: { font: 'Bricolage Grotesque', weight: 800 }, support: { font: 'Bricolage Grotesque', weight: 500 } },
  { key: 'plantao', name: 'Plantão', title: { font: 'Anton', weight: 400 }, support: { font: 'Barlow Condensed', weight: 600 } },
  { key: 'tabloide', name: 'Tabloide', title: { font: 'Archivo Black', weight: 400 }, support: { font: 'Barlow Condensed', weight: 600 } },
  { key: 'revista', name: 'Revista', title: { font: 'DM Serif Display', weight: 400 }, support: { font: 'Bricolage Grotesque', weight: 500 } },
  { key: 'editorial', name: 'Editorial', title: { font: 'Fraunces', weight: 800 }, support: { font: 'Fraunces', weight: 500, italic: true } },
  { key: 'esporte', name: 'Esporte', title: { font: 'Barlow Condensed', weight: 800, italic: true }, support: { font: 'Barlow Condensed', weight: 600 } },
];

/* ---------- elementos ---------- */

let idCounter = 0;

export function uid() {
  idCounter++;
  return 'el' + Date.now().toString(36) + idCounter;
}

function merge(base, props) {
  const out = { ...base };

  for (const key in props) {
    const value = props[key];
    const isObject = value && typeof value === 'object' && !Array.isArray(value);

    if (isObject && base[key] && typeof base[key] === 'object') {
      out[key] = { ...base[key], ...value };
    } else {
      out[key] = value;
    }
  }

  return out;
}

export function photoElement(props = {}) {
  return merge({
    id: uid(), type: 'photo', role: 'photo', name: 'Foto', hidden: false,
    x: 0, y: 0, w: 1, h: 0.4,
    zoom: 1, focusX: 0.5, focusY: 0.5,
    radius: 0, shadow: false,
    border: { width: 0, color: '#ffffff' },
    overlay: { type: 'none', color: '#000000', opacity: 0.7, direction: 'bottom' },
  }, props);
}

export function textElement(props = {}) {
  return merge({
    id: uid(), type: 'text', role: 'support', name: 'Texto', hidden: false,
    content: 'Texto',
    x: 0.074, y: 0.5, w: 0.852, h: 0.05,
    font: 'Poppins', weight: 500, italic: false, size: 36, minSize: 22,
    color: INK, align: 'left', valign: 'middle',
    uppercase: false, lineHeight: 1.2, letterSpacing: 0, maxLines: 2,
    shadow: false, onCard: false, onAccent: false,
    box: { enabled: false, mode: 'block', color: PURPLE, opacity: 1, padding: 16, radius: 6 },
  }, props);
}

export function titleElement(props = {}) {
  return textElement(merge({
    role: 'title', name: 'Título', content: '{titulo}',
    h: 0.2, font: 'Poppins', weight: 800, size: 72, minSize: 34,
    maxLines: 7, lineHeight: 1.12,
  }, props));
}

export function ctaElement(props = {}) {
  return textElement(merge({
    role: 'cta', name: 'Chamada', content: 'Arraste pra cima',
    h: 0.03, font: 'Poppins', weight: 600, size: 38, minSize: 24, maxLines: 1,
  }, props));
}

export function iconElement(props = {}) {
  return merge({
    id: uid(), type: 'icon', role: 'icon', name: 'Ícone', hidden: false,
    icon: 'chevron-up', color: INK, stroke: 10,
    x: 0.46, y: 0.75, w: 0.08, h: 0.025,
  }, props);
}

export function shapeElement(props = {}) {
  return merge({
    id: uid(), type: 'shape', role: 'card', name: 'Forma', hidden: false,
    color: '#ffffff', opacity: 1, radius: 0, shadow: false,
    x: 0.1, y: 0.1, w: 0.8, h: 0.2,
  }, props);
}

/* ---------- modelos prontos ---------- */

/*
 * Só o título (e no Story a chamada) é escrito na imagem; o resto é foto,
 * cor e forma.
 * Story: a área útil vai de 0.13 a 0.823 da altura (o resto fica sob a
 * interface do Instagram). Margem lateral de 80px (0.074).
 */
function storyTemplates() {
  return [
    {
      key: 'story-destaque', name: 'Destaque',
      config: {
        fontPair: 'i7',
        background: { type: 'color', color: INK },
        elements: [
          photoElement({ x: 0, y: 0, w: 1, h: 1, overlay: { type: 'gradient', color: '#0d0814', opacity: 0.8, direction: 'bottom' } }),
          titleElement({ y: 0.44, h: 0.28, size: 70, lineHeight: 1.28, valign: 'bottom', color: INK, box: { enabled: true, mode: 'lines', color: '#ffffff', padding: 18, radius: 0 } }),
          iconElement({ linkedTo: 'cta', x: 0.465, y: 0.752, w: 0.07, h: 0.02, stroke: 9, color: '#ffffff' }),
          ctaElement({ y: 0.793, align: 'center', color: '#ffffff', shadow: true }),
        ],
      },
    },
    {
      key: 'story-impacto', name: 'Impacto',
      config: {
        palette: 'plantao', fontPair: 'plantao',
        background: { type: 'color', color: RED },
        elements: [
          photoElement({ x: 0, y: 0, w: 1, h: 0.48 }),
          shapeElement({ role: 'accent', name: 'Faixa', x: 0.074, y: 0.522, w: 0.16, h: 0.007, color: '#ffffff' }),
          titleElement({ y: 0.54, h: 0.18, font: 'Anton', weight: 400, size: 98, lineHeight: 1.04, uppercase: true, valign: 'top', color: '#ffffff' }),
          iconElement({ linkedTo: 'cta', x: 0.465, y: 0.752, w: 0.07, h: 0.02, stroke: 9, color: '#ffffff' }),
          ctaElement({ y: 0.793, align: 'center', color: '#ffffff' }),
        ],
      },
    },
    {
      key: 'story-revista', name: 'Revista',
      config: {
        palette: 'noite', fontPair: 'revista',
        background: { type: 'color', color: INK },
        elements: [
          photoElement({ x: 0, y: 0, w: 1, h: 1, overlay: { type: 'gradient', color: '#0d0814', opacity: 0.85, direction: 'bottom' } }),
          titleElement({ y: 0.47, h: 0.25, font: 'DM Serif Display', weight: 400, size: 92, lineHeight: 1.04, align: 'center', valign: 'bottom', color: '#ffffff', shadow: true }),
          iconElement({ linkedTo: 'cta', x: 0.465, y: 0.752, w: 0.07, h: 0.02, stroke: 9, color: '#ffffff' }),
          ctaElement({ y: 0.793, align: 'center', color: '#ffffff', shadow: true }),
        ],
      },
    },
    {
      key: 'story-manchete', name: 'Manchete',
      config: {
        palette: 'noite', fontPair: 'tabloide',
        background: { type: 'color', color: INK },
        elements: [
          titleElement({ y: 0.13, h: 0.32, font: 'Archivo Black', weight: 400, size: 108, minSize: 48, lineHeight: 1.0, uppercase: true, valign: 'bottom', color: '#ffffff' }),
          shapeElement({ role: 'accent', name: 'Faixa', x: 0.074, y: 0.465, w: 0.852, h: 0.006, color: PINK }),
          photoElement({ x: 0.074, y: 0.483, w: 0.852, h: 0.237 }),
          iconElement({ linkedTo: 'cta', x: 0.465, y: 0.752, w: 0.07, h: 0.02, stroke: 9, color: '#ffffff' }),
          ctaElement({ y: 0.793, align: 'center', color: '#ffffff' }),
        ],
      },
    },
    {
      key: 'story-cartao', name: 'Cartão',
      config: {
        palette: 'i7', fontPair: 'moderno',
        background: { type: 'color', color: PURPLE },
        elements: [
          shapeElement({ name: 'Cartão', x: 0.074, y: 0.135, w: 0.852, h: 0.585, radius: 48, shadow: true }),
          photoElement({ x: 0.111, y: 0.156, w: 0.778, h: 0.3, radius: 30 }),
          titleElement({ x: 0.111, y: 0.48, w: 0.778, h: 0.22, font: 'Bricolage Grotesque', weight: 800, size: 68, lineHeight: 1.06, valign: 'middle', color: INK, onCard: true }),
          iconElement({ linkedTo: 'cta', x: 0.465, y: 0.752, w: 0.07, h: 0.02, stroke: 9, color: '#ffffff' }),
          ctaElement({ y: 0.793, align: 'center', color: '#ffffff' }),
        ],
      },
    },
    {
      key: 'story-dividido', name: 'Dividido',
      config: {
        palette: 'ceu', fontPair: 'i7',
        background: { type: 'color', color: '#0b3fa8' },
        elements: [
          photoElement({ x: 0, y: 0, w: 1, h: 0.48 }),
          titleElement({ y: 0.5, h: 0.22, size: 76, lineHeight: 1.08, valign: 'middle', color: '#ffffff' }),
          iconElement({ linkedTo: 'cta', x: 0.465, y: 0.752, w: 0.07, h: 0.02, stroke: 9, color: SUN }),
          ctaElement({ y: 0.793, align: 'center', color: SUN }),
        ],
      },
    },
    {
      key: 'story-classico', name: 'Clássico',
      config: {
        fontPair: 'i7',
        background: { type: 'gradient', colors: ['#fff6dc', '#f6d27a'], angle: 180 },
        elements: [
          photoElement({ x: 0, y: 0.155, w: 1, h: 0.36 }),
          titleElement({ x: 0.1, y: 0.545, w: 0.8, h: 0.175, weight: 700, size: 54, color: '#505050', align: 'center', uppercase: true }),
          iconElement({ linkedTo: 'cta', x: 0.455, y: 0.749, w: 0.09, h: 0.023, color: '#505050' }),
          ctaElement({ y: 0.793, align: 'center', color: '#505050' }),
        ],
      },
    },
  ];
}

/* Post: sem chamada, só foto e título. Margem de 80px (0.074). */
function postTemplates() {
  return [
    {
      key: 'post-destaque', name: 'Destaque',
      config: {
        fontPair: 'i7',
        background: { type: 'color', color: INK },
        elements: [
          photoElement({ x: 0, y: 0, w: 1, h: 1, overlay: { type: 'gradient', color: '#0d0814', opacity: 0.8, direction: 'bottom' } }),
          titleElement({ y: 0.4, h: 0.526, size: 60, lineHeight: 1.3, valign: 'bottom', color: INK, box: { enabled: true, mode: 'lines', color: '#ffffff', padding: 16, radius: 0 } }),
        ],
      },
    },
    {
      key: 'post-impacto', name: 'Impacto',
      config: {
        palette: 'plantao', fontPair: 'plantao',
        background: { type: 'color', color: RED },
        elements: [
          photoElement({ x: 0, y: 0, w: 1, h: 0.56 }),
          titleElement({ y: 0.6, h: 0.326, font: 'Anton', weight: 400, size: 72, lineHeight: 1.04, uppercase: true, valign: 'middle', color: '#ffffff' }),
        ],
      },
    },
    {
      key: 'post-revista', name: 'Revista',
      config: {
        palette: 'noite', fontPair: 'revista',
        background: { type: 'color', color: INK },
        elements: [
          photoElement({ x: 0, y: 0, w: 1, h: 1, overlay: { type: 'gradient', color: '#0d0814', opacity: 0.85, direction: 'bottom' } }),
          titleElement({ y: 0.44, h: 0.486, font: 'DM Serif Display', weight: 400, size: 72, lineHeight: 1.04, align: 'center', valign: 'bottom', color: '#ffffff', shadow: true }),
        ],
      },
    },
    {
      key: 'post-manchete', name: 'Manchete',
      config: {
        palette: 'noite', fontPair: 'tabloide',
        background: { type: 'color', color: INK },
        elements: [
          titleElement({ y: 0.074, h: 0.37, font: 'Archivo Black', weight: 400, size: 78, minSize: 36, lineHeight: 1.0, uppercase: true, valign: 'bottom', color: '#ffffff' }),
          shapeElement({ role: 'accent', name: 'Faixa', x: 0.074, y: 0.468, w: 0.852, h: 0.008, color: PINK }),
          photoElement({ x: 0.074, y: 0.492, w: 0.852, h: 0.434 }),
        ],
      },
    },
    {
      key: 'post-cartao', name: 'Cartão',
      config: {
        palette: 'i7', fontPair: 'moderno',
        background: { type: 'color', color: PURPLE },
        elements: [
          shapeElement({ name: 'Cartão', x: 0.074, y: 0.074, w: 0.852, h: 0.852, radius: 44, shadow: true }),
          photoElement({ x: 0.111, y: 0.111, w: 0.778, h: 0.46, radius: 28 }),
          titleElement({ x: 0.111, y: 0.6, w: 0.778, h: 0.29, font: 'Bricolage Grotesque', weight: 800, size: 54, lineHeight: 1.06, valign: 'middle', color: INK, onCard: true }),
        ],
      },
    },
    {
      key: 'post-dividido', name: 'Dividido',
      config: {
        palette: 'ceu', fontPair: 'i7',
        background: { type: 'color', color: '#0b3fa8' },
        elements: [
          photoElement({ x: 0, y: 0, w: 0.5, h: 1 }),
          titleElement({ x: 0.55, y: 0.074, w: 0.376, h: 0.852, size: 50, maxLines: 11, lineHeight: 1.08, valign: 'middle', color: '#ffffff' }),
        ],
      },
    },
    {
      key: 'post-classico', name: 'Clássico',
      config: {
        fontPair: 'i7',
        background: { type: 'color', color: '#ffffff' },
        elements: [
          photoElement({ x: 0, y: 0, w: 1, h: 0.62 }),
          titleElement({ y: 0.65, h: 0.3, weight: 700, size: 50, align: 'center', color: INK }),
        ],
      },
    },
  ];
}

/*
 * Os modelos são gerados por função para cada aplicação receber elementos com
 * ids novos e não compartilhar objetos entre si.
 */
export function getPresetTemplates(format) {
  let list = postTemplates();

  if (format === 'story') {
    list = storyTemplates();
  }

  return list.map(template => ({
    ...template,
    format,
    config: { version: 1, ...template.config },
  }));
}

export function findPresetTemplate(format, key) {
  return getPresetTemplates(format).find(template => template.key === key) || null;
}
